import '@fontsource-variable/manrope';
import '@fontsource-variable/cairo';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { hashKey, MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { companyStore, onDataChanged, tenantStore } from './api/client';
import { App } from './App';
import { AuthProvider } from './auth/AuthContext';
import { describeError, toast, ToastProvider } from './components/Toast';
import { BrandingProvider } from './theme/branding';
import { DirectionProvider } from './theme/DirectionProvider';
import { initI18n } from './i18n';
import { startServiceWorker } from './pwa';

const queryClient = new QueryClient({
  /**
   * A mutation that failed with nobody listening used to fail silently: the
   * button un-greyed and nothing said why. Anything without its own onError,
   * and not marked as showing its error inline (`meta: { inlineError: true }`),
   * reports here instead.
   */
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError || mutation.meta?.inlineError) return;
      toast.error(describeError(error));
    },
  }),
  defaultOptions: {
    queries: {
      /**
       * Every cached answer belongs to the workspace and company it was asked
       * in.
       *
       * Query keys name a doctype and a filter — `['link-options', 'Branch']` —
       * and every request already carries the workspace in a header, so two
       * customers' branches were the same key holding different rows. Signing
       * out and into another workspace left the first one's list behind every
       * dropdown in the app: served at once from cache, corrected only by a
       * background refetch nobody waits for. Folding the two into the hash
       * makes that structurally impossible rather than something each new page
       * has to remember.
       */
      queryKeyHashFn: (key) => hashKey([tenantStore.get(), companyStore.get(), ...key]),
      /**
       * How long an answer is shown without asking again when a screen opens
       * or the window regains focus. Short, because a colleague, an
       * administrator or a background job may have changed it since; writes
       * made from this browser do not wait for it at all (onDataChanged below).
       *
       * Refetching on focus was switched off once, and with it the only way a
       * change made elsewhere reached a screen left open in another tab. It is
       * the default again: coming back to the tab is exactly when stale data
       * would be read and acted on. Background refetches keep the data on
       * screen while they run, so nothing flickers.
       */
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        // Auth and permission failures are final; retrying just burns requests.
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403) return false;
        return failureCount < 2;
      },
    },
  },
});

/**
 * Any write that succeeded — a mutation, a dialog's save, an import, a bulk
 * job — marks every cached answer stale (see api/freshness.ts). What is on
 * screen refetches now, the rest when next shown. Only answers marked
 * `meta: { static: true }` (the help index shipped with the build) are left.
 */
onDataChanged(() => {
  void queryClient.invalidateQueries({ predicate: (query) => query.meta?.static !== true });
});

/**
 * A data router so screens can hold navigation while they have unsaved edits
 * (`useBlocker`). The app's own route table stays in <App/>'s <Routes>, served
 * from one splat route here.
 */
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <AuthProvider>
        <App />
      </AuthProvider>
    ),
  },
]);

async function bootstrap(): Promise<void> {
  startServiceWorker();
  await initI18n();

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrandingProvider>
          <DirectionProvider>
            <ToastProvider>
              <RouterProvider router={router} />
            </ToastProvider>
          </DirectionProvider>
        </BrandingProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

void bootstrap();
